import {useEffect, useRef, useState} from 'react';

import {useCurrentUser} from 'src/hooks/useCurrentUser';
import {getDraftConnection} from 'src/openapi/admin/apis/SsoV2Api';
import {SsoStatus} from 'src/openapi/admin/models/SsoStatus';
import {useAppDispatch} from 'src/store/hooks';

import type {DraftConnectionResponse} from 'src/openapi/admin/models/DraftConnectionResponse';
import type {SsoStep} from 'src/openapi/admin/models/SsoStep';
import type {SsoUserInfo} from 'src/openapi/admin/models/SsoUserInfo';

type UseDraftConnectionReturn = {
    loading: boolean,
    draft: DraftConnectionResponse | null,
    error: string | null,
    isOwnedByOtherUser: boolean,
    otherUser: SsoUserInfo | null,
    otherUserStep: SsoStep | null,
    status: SsoStatus,
};

export function useDraftConnection(): UseDraftConnectionReturn {
    const dispatch = useAppDispatch();
    const {currentUser} = useCurrentUser();
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState<DraftConnectionResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isOwnedByOtherUser, setIsOwnedByOtherUser] = useState(false);
    const [otherUser, setOtherUser] = useState<SsoUserInfo | null>(null);
    const [otherUserStep, setOtherUserStep] = useState<SsoStep | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [status, setStatus] = useState<SsoStatus>(SsoStatus.IN_PROGRESS);

    useEffect(() => {
        if (currentUser == null) {
            return;
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const response = await dispatch(
                    getDraftConnection({signal: controller.signal}),
                );

                if (controller.signal.aborted) {
                    return;
                }

                if (response.payload?.success && response.payload.data != null) {
                    const draftData = response.payload.data as DraftConnectionResponse;
                    setStatus(draftData.status);
                    if (draftData.createdBy.userId !== currentUser.userId) {
                        setIsOwnedByOtherUser(true);
                        setOtherUser(draftData.createdBy);
                        setOtherUserStep(draftData.latestStep);
                        setDraft(null);
                    } else {
                        setDraft(draftData);
                        setIsOwnedByOtherUser(false);
                    }
                } else {
                    setDraft(null);
                    setIsOwnedByOtherUser(false);
                }
            } catch (err) {
                if ((err as {name?: string})?.name === 'AbortError') {
                    return;
                }
                setDraft(null);
                setError('Failed to load SSO draft. Please refresh and try again.');
            } finally {
                if (abortControllerRef.current === controller) {
                    abortControllerRef.current = null;
                }
                setLoading(false);
            }
        })();

        return () => {
            controller.abort();
        };
    }, [dispatch, currentUser]);

    return {loading, draft, error, isOwnedByOtherUser, otherUser, otherUserStep, status};
}
